/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2023, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import _ from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useMount } from 'react-use';

import { useIsNewIA } from '@ibm-watson/shared/modules/ia/hooks/useIsNewIA';
import {
  isFeatureEnabled,
  selectIsGenesysAudioConnectorInCpd
} from '@ibm-watson/shared/modules/ia/store/features/selectors';
import { isCrn } from '@ibm-watson/shared/modules/ia/util/crn';
import { useAppDispatch, useAppSelector } from '@ibm-watson/shared/store';
import {
  FIELDS as PHONE_FIELDS,
  phoneGenesysAudioConnectorSchema,
  phoneGenesysAudioConnectorSchemaInCpd,
  phoneSettingsSchema,
  phoneSoeSettingsSchema
} from '../components/phone/schema';
import {
  createIntegration,
  setIntegrationInitializing,
  updateIntegration
} from '../store/channels/actions';
import { updateIntegration as updateClassicIntegration } from '../store/channels/classic/actions';
import {
  selectDraftIntegration,
  selectIsIntegrationInitializing
} from '../store/channels/selectors';
import {
  STTInstanceCreateNotRequired,
  STTInstanceCreateRequired,
  TTSInstanceCreateNotRequired,
  TTSInstanceCreateRequired
} from '../store/phone/actions';
import {
  listSpeechToTextInstancesIfRequired,
  listTextToSpeechInstancesIfRequired,
  loadSpeechToTextInstance,
  loadTextToSpeechInstance
} from '../store/voice/actions';
import {
  selectCurrentSpeechToTextInstance,
  selectCurrentTextToSpeechInstance,
  selectDefaultSpeechToTextModel,
  selectDefaultTextToSpeechModel,
  selectPreferredSpeechToTextInstance,
  selectPreferredTextToSpeechInstance,
  selectSpeechToTextInstances,
  selectSpeechToTextInstanceLoaded,
  selectTextToSpeechInstances,
  selectTextToSpeechInstanceLoaded,
  selectSpeechToTextListLoadFailed,
  selectTextToSpeechListLoadFailed,
  selectTextToSpeechListLoaded,
  selectSpeechToTextListLoaded
} from '../store/voice/selectors';
import {
  GENESYS_AUDIO_CONNECTOR_TYPE,
  PHONE_CHANNEL_TYPE
} from '../util/channelTypes';
import { INTEGRATIONS_FEATURE_FLAGS } from '../util/integrationsUtil';
import { useFormHelpers } from './useFormHelpers';

/**
 * This hook is called at the beginning of a phone integration's
 * authoring and is used to set up the integration and supply it with
 * the correct speech-to-text and text-to-speech instances. This
 * hook is essentially comprised of five useMount/useEffect steps
 * run sequentially:
 * 1. Mark the start of the integration initialization and load all
 *    STT and TTS instances.
 * 2. Check the draft phone integration for existing STT and TTS
 *    instance information in the settings. If both STT and TTS
 *    config settings exist, skip steps 4 and 5 and then finish.
 * 3. Load the current/selected STT and TTS instances concurrently.
 * 4. Update both the form-watch integration settings and the draft
 *    integration settings with the loaded STT+TTS settings.
 * 5. Either create or update the draft integration to the backend.
 */
export const useInitializeSpeech = (
  assistantId,
  environmentId,
  formMethods,
  updateClassicIntegrationOverlay
) => {
  const dispatch = useAppDispatch();
  const isNewIA = useIsNewIA();
  const { getFieldValue, updateFieldValue } = useFormHelpers(formMethods);
  const { reset, trigger } = formMethods;

  const draftIntegration = useAppSelector(selectDraftIntegration);

  const isPhoneInitializing = useAppSelector(selectIsIntegrationInitializing);
  const isSoeFeatureEnabled = useAppSelector(state =>
    isFeatureEnabled(state, INTEGRATIONS_FEATURE_FLAGS.SOE_ENABLED)
  );
  // Speech to Text selectors
  const STTInstances = useAppSelector(selectSpeechToTextInstances);
  const STTCurrentInstance = useAppSelector(selectCurrentSpeechToTextInstance);
  const STTDefaultInstance = useAppSelector(
    selectPreferredSpeechToTextInstance
  );

  const STTInstanceLoaded = useAppSelector(selectSpeechToTextInstanceLoaded);
  const STTListLoaded = useAppSelector(selectSpeechToTextListLoaded);
  const STTListLoadedFailed = useAppSelector(selectSpeechToTextListLoadFailed);
  const STTDefaultModel = useAppSelector(selectDefaultSpeechToTextModel);
  // Text to Speech selectors
  const TTSInstances = useAppSelector(selectTextToSpeechInstances);
  const TTSCurrentInstance = useAppSelector(selectCurrentTextToSpeechInstance);
  const TTSDefaultInstance = useAppSelector(
    selectPreferredTextToSpeechInstance
  );

  const TTSInstanceLoaded = useAppSelector(selectTextToSpeechInstanceLoaded);
  const TTSListLoaded = useAppSelector(selectTextToSpeechListLoaded);
  const TTSListLoadedFailed = useAppSelector(selectTextToSpeechListLoadFailed);
  const TTSDefaultModel = useAppSelector(selectDefaultTextToSpeechModel);

  const isGenesysAudioConnectorEnabledInCpd = useAppSelector(
    selectIsGenesysAudioConnectorInCpd
  );

  // Denotes whether at least one field was updated, and an update
  // integration is needed by the end of this hook.
  const [fieldsUpdated, setFieldsUpdated] = useState<boolean>(false);

  const [needsCreation, setNeedsCreation] = useState<boolean>(false);
  const [hasCreated, setHasCreated] = useState<boolean>(false);

  // Completion state for step 2: check if integration needs updating
  const [checkedDraftIntegration, setCheckedDraftIntegration] =
    useState<boolean>(false);
  // Completion states for step 3: loading active STT/TTS instance
  const [loadSTTInstanceCalled, setLoadSTTInstanceCalled] =
    useState<boolean>(false);
  const [loadTTSInstanceCalled, setLoadTTSInstanceCalled] =
    useState<boolean>(false);
  // Completion state for step 4: update settings with STT/TTS fields
  const [updateSettingsFieldsComplete, setUpdateSettingsFieldsComplete] =
    useState<boolean>(false);
  // Completion state for step 5: save draft integration to backend
  const [updateDraftIntegrationComplete, setUpdateDraftIntegrationComplete] =
    useState<boolean>(false);

  // Classic IA splash pages need to halt creating the integration
  // and leave the creation to be done by the Create button in the
  // splash page. Classic IA Phone integrations distinctly have the
  // `updateClassicIntegrationOverlay` so that can be used to denote
  // if we are in classic IA phone authoring.
  const isClassicIANonSplashPage = !_.isNil(updateClassicIntegrationOverlay);

  const isGenesysAudioConnectorSelected =
    draftIntegration?.type === PHONE_CHANNEL_TYPE &&
    draftIntegration?.settings?.contact_center?.provider ===
      GENESYS_AUDIO_CONNECTOR_TYPE;

  const settingsSchema = isSoeFeatureEnabled
    ? phoneSoeSettingsSchema
    : isGenesysAudioConnectorEnabledInCpd
    ? phoneGenesysAudioConnectorSchemaInCpd
    : isGenesysAudioConnectorSelected
    ? phoneGenesysAudioConnectorSchema
    : phoneSettingsSchema;

  /**
   * Update a field inside both draft integration and form-watch
   * integration settings, with the value specified. If an edit was
   * made, update the `fieldsUpdated` useState.
   *
   * @param {string} fieldName - name of the field inside settings
   * @param value - the value to set
   */
  const updateDraftSettings = useCallback(
    (fieldName: string, value: any) => {
      if (!_.isEqual(_.get(draftIntegration?.settings, fieldName), value)) {
        _.set(draftIntegration?.settings, fieldName, value);
        setFieldsUpdated(true);
      }

      updateFieldValue(fieldName, value);
    },
    [draftIntegration, updateFieldValue]
  );

  /**
   * Create the integration appropriately for new/classic IA
   */
  const createPhone = useCallback(() => {
    if (isNewIA) {
      if (_.isNil(draftIntegration.integration_id) && !hasCreated) {
        dispatch(
          createIntegration({
            assistantId,
            environmentId,
            integration: draftIntegration
          })
        );
      } else {
        dispatch(
          updateIntegration({
            assistantId,
            environmentId,
            integration: draftIntegration
          })
        );
      }
    } else {
      dispatch(
        updateClassicIntegration({
          assistantId: draftIntegration.assistant_id,
          integrationId: draftIntegration.integration_id,
          integration: draftIntegration
        })
      );
      updateClassicIntegrationOverlay();
    }
  }, [
    isNewIA,
    draftIntegration,
    hasCreated,
    dispatch,
    assistantId,
    environmentId,
    updateClassicIntegrationOverlay
  ]);

  /**
   * Step 1A: List all STT/TTS instances.
   */
  useMount(() => {
    /*
      In CPD, Genesys Audio Connector has a different flow of
      connecting to STT and TTS instances. No need to make a call
      to get a list of those instances in CPD.
    */
    if (!isGenesysAudioConnectorEnabledInCpd) {
      // Set the integration initializing state to true. By the end of
      // this hook, we will set this state back to false.
      dispatch(setIntegrationInitializing(true));
      dispatch(listSpeechToTextInstancesIfRequired());
      dispatch(listTextToSpeechInstancesIfRequired());
    } else {
      // Only create a new phone integration when its new IA
      // and there is no existing integration setup
      if (isNewIA && _.isNil(draftIntegration?.integration_id)) {
        setNeedsCreation(true);
      }
    }
  });

  /**
   * Step 1B: Check if we need to create the STT instance.
   *
   * We need to create the instance, if:
   * - we are not loading the instance, AND
   * - there are no existing instances
   *
   * Additionally, we create the instance on enterprise, if:
   * - we need to create the instance generally, AND
   * - the instance is in the isolated enterprise plan, AND
   * - there isn't already a premium speech instance
   */
  useEffect(() => {
    if (STTListLoadedFailed || STTListLoaded) {
      if (STTInstances?.length === 0) {
        dispatch(STTInstanceCreateRequired());
        dispatch(setIntegrationInitializing(false));
        // Create integration so it can still be used when Speech instances
        // need to be created
        if (
          isNewIA &&
          _.isNil(draftIntegration?.integration_id) &&
          !hasCreated &&
          !_.isNil(draftIntegration)
        ) {
          setNeedsCreation(true);
        }
      } else {
        dispatch(STTInstanceCreateNotRequired());
      }
    }
  }, [
    STTInstanceLoaded,
    STTInstances?.length,
    STTListLoaded,
    STTListLoadedFailed,
    createPhone,
    dispatch,
    draftIntegration,
    draftIntegration?.integration_id,
    hasCreated,
    isNewIA
  ]);

  /**
   * Step 1C: Check if we need to create TTS instance.
   *
   * We need to create the instance, if:
   * - we are not loading the instance, AND
   * - there are no existing instances
   *
   * Additionally, we create the instance on enterprise, if:
   * - we need to create the instance generally, AND
   * - the instance is in the isolated enterprise plan, AND
   * - there isn't already a premium speech instance
   */
  useEffect(() => {
    if (TTSListLoadedFailed || TTSListLoaded) {
      if (TTSInstances?.length === 0) {
        dispatch(TTSInstanceCreateRequired());
        dispatch(setIntegrationInitializing(false));
        if (
          isNewIA &&
          _.isNil(draftIntegration?.integration_id) &&
          !hasCreated &&
          !_.isNil(draftIntegration)
        ) {
          setNeedsCreation(true);
        }
      } else {
        dispatch(TTSInstanceCreateNotRequired());
      }
    }
  }, [
    STTInstanceLoaded,
    TTSInstanceLoaded,
    TTSInstances,
    TTSListLoaded,
    TTSListLoadedFailed,
    createPhone,
    dispatch,
    draftIntegration,
    draftIntegration?.integration_id,
    hasCreated,
    isNewIA
  ]);

  /**
   * Step 1D: Create integration if needed. Only if in new IA and there are no STT or TTS instances set.
   */

  useEffect(() => {
    if (needsCreation && !hasCreated) {
      setHasCreated(true);
      createPhone();
    }
  }, [needsCreation, hasCreated, createPhone]);

  /**
   * Step 2: Check draft integration for STT/TTS instances.
   */
  useEffect(() => {
    // Skip this step if:
    // - The entire hook has already run previously
    // - This step has already run previously
    // - The draft integration is empty / not ready
    if (
      !updateDraftIntegrationComplete &&
      !checkedDraftIntegration &&
      !_.isEmpty(draftIntegration)
    ) {
      const draftSTTInstanceId = _.get(
        draftIntegration.settings,
        PHONE_FIELDS.STT.INSTANCE_ID
      );
      const draftTTSInstanceId = _.get(
        draftIntegration.settings,
        PHONE_FIELDS.TTS.INSTANCE_ID
      );

      // If either draft STT or TTS, then we can't fully load the STT
      // or TTS settings onto the phone integration. Also, if we are
      // in classic IA phone authoring, we should leave the creation
      // to Tanya manually clicking Create in the splash page.
      if (
        !_.isEmpty(draftSTTInstanceId) ||
        !_.isEmpty(draftTTSInstanceId) ||
        isClassicIANonSplashPage
      ) {
        dispatch(setIntegrationInitializing(false));
      }

      setCheckedDraftIntegration(true);
    }
  }, [
    checkedDraftIntegration,
    dispatch,
    draftIntegration,
    isClassicIANonSplashPage,
    updateDraftIntegrationComplete
  ]);

  /**
   * Step 3A: Load the proper STT instance once all STT instances
   * are listed.
   */
  useEffect(() => {
    // Skip this step if:
    // - This step has already run previously
    // - A load STT action was already called previously
    // - The list of STT instances is empty / we cannot select a STT
    //   instance to load
    if (
      checkedDraftIntegration &&
      !loadSTTInstanceCalled &&
      !_.isEmpty(STTInstances)
    ) {
      const formSTTInstanceId = getFieldValue(PHONE_FIELDS.STT.INSTANCE_ID);
      const STTid = !_.isEmpty(formSTTInstanceId)
        ? formSTTInstanceId
        : STTDefaultInstance?.id;
      // If the ID isn't a CRN, then we can't load it.
      if (isCrn(STTid)) {
        dispatch(loadSpeechToTextInstance(STTid));
        setLoadSTTInstanceCalled(true);
      }
    }
  }, [
    checkedDraftIntegration,
    dispatch,
    getFieldValue,
    loadSTTInstanceCalled,
    STTDefaultInstance?.id,
    STTInstances
  ]);

  /**
   * Step 3B: Load the proper TTS instance once all TTS instances
   * are listed.
   */
  useEffect(() => {
    // Skip this step if:
    // - This stpe has already run previously
    // - A load TTS action was already called previously
    // - The list of TTS instances is empty / we cannot select a TTS
    //   instance to load
    if (
      checkedDraftIntegration &&
      !loadTTSInstanceCalled &&
      !_.isEmpty(TTSInstances)
    ) {
      const formTTSInstanceId = getFieldValue(PHONE_FIELDS.TTS.INSTANCE_ID);
      const TTSid = !_.isEmpty(formTTSInstanceId)
        ? formTTSInstanceId
        : TTSDefaultInstance?.id;
      // IF the ID isn't a CRN, then we can't load it.
      if (isCrn(TTSid)) {
        dispatch(loadTextToSpeechInstance(TTSid));
        setLoadTTSInstanceCalled(true);
      }
    }
  }, [
    checkedDraftIntegration,
    dispatch,
    getFieldValue,
    loadTTSInstanceCalled,
    TTSDefaultInstance?.id,
    TTSInstances
  ]);

  /**
   * Step 4: Update all STT/TTS fields into the draft integration.
   */
  useEffect(() => {
    // Skip this step if:
    // - This hook has already or previously finished running
    // - Load STT instance action was not called
    // - A STT instance was not loaded
    // - Load TTS instance action was not called
    // - A TTS instance was not loaded
    if (
      isPhoneInitializing &&
      loadSTTInstanceCalled &&
      STTInstanceLoaded &&
      loadTTSInstanceCalled &&
      TTSInstanceLoaded
    ) {
      updateDraftSettings(PHONE_FIELDS.STT.INSTANCE_ID, STTCurrentInstance?.id);
      updateDraftSettings(
        PHONE_FIELDS.STT.URL,
        STTCurrentInstance?.credentials?.url
      );
      updateDraftSettings(
        PHONE_FIELDS.STT.APIKEY,
        STTCurrentInstance?.credentials?.apikey
      );
      updateDraftSettings(PHONE_FIELDS.STT.NARROWBAND.MODEL, STTDefaultModel);

      updateDraftSettings(PHONE_FIELDS.TTS.INSTANCE_ID, TTSCurrentInstance?.id);
      updateDraftSettings(
        PHONE_FIELDS.TTS.URL,
        TTSCurrentInstance?.credentials?.url
      );
      updateDraftSettings(
        PHONE_FIELDS.TTS.APIKEY,
        TTSCurrentInstance?.credentials?.apikey
      );
      updateDraftSettings(PHONE_FIELDS.TTS.VOICE, TTSDefaultModel);
      setUpdateSettingsFieldsComplete(true);
    }
  }, [
    draftIntegration,
    isPhoneInitializing,
    loadSTTInstanceCalled,
    loadTTSInstanceCalled,
    STTCurrentInstance,
    STTDefaultModel,
    STTInstanceLoaded,
    TTSCurrentInstance,
    TTSDefaultModel,
    TTSInstanceLoaded,
    updateDraftSettings
  ]);

  /**
   * Step 5: Save and update the draft integration. Also reset the
   * draft integration form context.
   */
  useEffect(() => {
    // Skip this step if:
    // - A create/update integration was previously called
    // - Updating draft and watch-form integrations settings was not
    //   conducted before this step
    if (!updateDraftIntegrationComplete && updateSettingsFieldsComplete) {
      // Only post a create/update if at least one field was updated
      if (fieldsUpdated) {
        createPhone();
      }

      // We need to remove private settings before using it again
      _.unset(draftIntegration?.settings, 'private.secrets');

      reset(settingsSchema.cast(_.cloneDeep(draftIntegration?.settings)));
      trigger();

      setUpdateDraftIntegrationComplete(true);
      // Mark this hook as finished, so none of the steps in this
      // hook are run again
      dispatch(setIntegrationInitializing(false));
    }
  }, [
    createPhone,
    dispatch,
    draftIntegration?.settings,
    fieldsUpdated,
    reset,
    settingsSchema,
    trigger,
    updateDraftIntegrationComplete,
    updateSettingsFieldsComplete
  ]);
};
